/**
 * Checklist-item wrapper around `WorkspaceFilePicker`. The cap lives here so a
 * JSONB checklist row cannot grow without bound. Converts between the
 * checklist attachment shape (fileId / folderId) and the picker's WorkspaceFileRef.
 */

import React, { useMemo } from 'react';

import {
  MAX_CHECKLIST_FILES,
  type ChecklistFileAttachment,
} from '../actionChecklistModel';
import WorkspaceFilePicker, {
  type WorkspaceFileRef,
} from './WorkspaceFilePicker';

type ChecklistFilePickerProps = {
  /** Absent for an org-scoped action, which can still pick from the org. */
  incidentId?: string | null;
  selected: ChecklistFileAttachment[];
  onChange: (attachments: ChecklistFileAttachment[]) => void;
  /** When true (HWCG), folders can be attached the same way as files. */
  allowFolders?: boolean;
};

const toPickerRefs = (
  attachments: ChecklistFileAttachment[]
): WorkspaceFileRef[] => {
  const out: WorkspaceFileRef[] = [];
  for (const row of attachments) {
    if (row.kind === 'folder') {
      if (!row.folderId) continue;
      out.push({
        fileId: row.folderId,
        label: row.label,
        kind: 'folder',
      });
      continue;
    }
    if (!row.fileId) continue;
    out.push({
      fileId: row.fileId,
      label: row.label,
      kind: 'file',
    });
  }
  return out;
};

const fromPickerRefs = (
  refs: WorkspaceFileRef[]
): ChecklistFileAttachment[] =>
  refs.map((file) =>
    file.kind === 'folder'
      ? {
          kind: 'folder' as const,
          folderId: file.fileId,
          label: file.label,
        }
      : {
          kind: 'file' as const,
          fileId: file.fileId,
          label: file.label,
        }
  );

const ChecklistFilePicker: React.FC<ChecklistFilePickerProps> = ({
  incidentId,
  selected,
  onChange,
  allowFolders = false,
}) => {
  const pickerSelected = useMemo(() => toPickerRefs(selected), [selected]);
  return (
    <WorkspaceFilePicker
      incidentId={incidentId}
      selected={pickerSelected}
      onChange={(next) => onChange(fromPickerRefs(next))}
      maxFiles={MAX_CHECKLIST_FILES}
      allowFolders={allowFolders}
    />
  );
};

export default ChecklistFilePicker;
